import type { Workout } from '../types';
import { localDateKeyFromIso } from './dateKey';
import { kgToLbs } from './units';

export function workoutSetCount(w: Workout): number {
  return w.exercises.reduce((s, e) => s + e.sets.length, 0);
}

export function workoutExerciseCount(w: Workout): number {
  return w.exercises.length;
}

export function workoutVolumeKg(w: Workout): number {
  let vol = 0;
  for (const ex of w.exercises) {
    for (const s of ex.sets) {
      if (s.isBodyweight) continue;
      const reps = s.trackMode === 'time' ? 1 : Math.max(0, s.reps);
      vol += reps * s.weightKg;
    }
  }
  return vol;
}

export function formatVolume(w: Workout, imperial: boolean): string | null {
  const volKg = workoutVolumeKg(w);
  if (volKg <= 0) return null;
  const vol = imperial ? kgToLbs(volKg) : volKg;
  const unit = imperial ? 'lb' : 'kg';
  if (vol >= 1000) return `${(vol / 1000).toFixed(1)}k ${unit}`;
  return `${Math.round(vol)} ${unit}`;
}

export function weeklyVolumeKg(workouts: Workout[], sinceMs: number): number {
  return workouts
    .filter((w) => new Date(w.startedAt).getTime() >= sinceMs)
    .reduce((sum, w) => sum + workoutVolumeKg(w), 0);
}

export function workoutsThisWeek(workouts: Workout[]): Workout[] {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - now.getDay());
  start.setHours(0, 0, 0, 0);
  return workouts.filter((w) => new Date(w.startedAt) >= start);
}

export function workoutsSince(workouts: Workout[], sinceMs: number): Workout[] {
  return workouts.filter((w) => new Date(w.startedAt).getTime() >= sinceMs);
}

export function workoutsThisMonth(workouts: Workout[]): Workout[] {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return workouts.filter((w) => new Date(w.startedAt) >= start);
}

export function monthlyVolumeKg(workouts: Workout[], sinceMs?: number): number {
  const cutoff = sinceMs ?? new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
  return weeklyVolumeKg(workouts, cutoff);
}

export function sortWorkoutsNewest(workouts: Workout[]): Workout[] {
  return [...workouts].sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
}

export function formatVolumeKg(volKg: number, imperial: boolean): string {
  if (volKg <= 0) return '—';
  const vol = imperial ? kgToLbs(volKg) : volKg;
  const unit = imperial ? 'lb' : 'kg';
  if (vol >= 1000) return `${(vol / 1000).toFixed(1)}k ${unit}`;
  return `${Math.round(vol)} ${unit}`;
}

const MUSCLE_LABELS: Record<string, string> = {
  chest: 'Chest',
  back: 'Back',
  legs: 'Legs',
  shoulders: 'Shoulders',
  arms: 'Arms',
  core: 'Core',
  cardio: 'Cardio',
  other: 'Other',
};

function normalizeMuscleGroup(raw: string, exerciseName: string): string {
  const key = raw.trim().toLowerCase();
  if (key && MUSCLE_LABELS[key]) return key;
  const lower = exerciseName.toLowerCase();
  if (/bench|chest|fly|push-up|pushup|dip/.test(lower)) return 'chest';
  if (/row|pull|lat|deadlift|pulldown|chin/.test(lower)) return 'back';
  if (/squat|leg|lunge|calf|hamstring|quad|hip thrust/.test(lower)) return 'legs';
  if (/shoulder|ohp|lateral|raise|shrug/.test(lower)) return 'shoulders';
  if (/curl|tricep|bicep|extension|skull/.test(lower)) return 'arms';
  if (/plank|crunch|ab|core|twist|hollow/.test(lower)) return 'core';
  if (/run|bike|cardio|treadmill|rower|jump|walk|swim/.test(lower)) return 'cardio';
  return 'other';
}

export type MuscleVolumeRow = { key: string; label: string; volumeKg: number; sets: number };

export function muscleGroupVolume(workouts: Workout[]): MuscleVolumeRow[] {
  const map = new Map<string, { volumeKg: number; sets: number }>();
  for (const w of workouts) {
    for (const ex of w.exercises) {
      const key = normalizeMuscleGroup(ex.muscleGroup, ex.name);
      const row = map.get(key) ?? { volumeKg: 0, sets: 0 };
      for (const s of ex.sets) {
        row.sets += 1;
        if (!s.isBodyweight) {
          const reps = s.trackMode === 'time' ? 1 : Math.max(0, s.reps);
          row.volumeKg += reps * s.weightKg;
        }
      }
      map.set(key, row);
    }
  }
  return [...map.entries()]
    .map(([key, v]) => ({
      key,
      label: MUSCLE_LABELS[key] ?? key,
      volumeKg: v.volumeKg,
      sets: v.sets,
    }))
    .sort((a, b) => b.volumeKg - a.volumeKg || b.sets - a.sets);
}

export function estimatedOneRepMaxKg(weightKg: number, reps: number): number {
  if (reps <= 0 || weightKg <= 0) return 0;
  if (reps === 1) return weightKg;
  return weightKg * (1 + reps / 30);
}

export type PersonalRecord = {
  exerciseName: string;
  weightKg: number;
  reps: number;
  estimated1RmKg: number;
  date: string;
  workoutId: string;
};

export function findPersonalRecords(workouts: Workout[]): PersonalRecord[] {
  const best = new Map<string, PersonalRecord>();
  for (const w of workouts) {
    for (const ex of w.exercises) {
      for (const s of ex.sets) {
        if (s.isBodyweight || s.weightKg <= 0) continue;
        const reps = s.trackMode === 'time' ? 1 : Math.max(1, s.reps);
        const e1rm = estimatedOneRepMaxKg(s.weightKg, reps);
        const key = ex.name.trim().toLowerCase();
        const prev = best.get(key);
        if (!prev || e1rm > prev.estimated1RmKg) {
          best.set(key, {
            exerciseName: ex.name,
            weightKg: s.weightKg,
            reps,
            estimated1RmKg: e1rm,
            date: w.startedAt,
            workoutId: w.id,
          });
        }
      }
    }
  }
  return [...best.values()].sort((a, b) => b.estimated1RmKg - a.estimated1RmKg);
}

export function workoutDatesSet(workouts: Workout[]): Set<string> {
  return new Set(workouts.map((w) => localDateKeyFromIso(w.startedAt)));
}
